import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface ContaReceberType {
    id: number;
    valor: number;
    taxa: number;
    vencimento: string;
    dataEmissao: string;
    dataBaixa: string;
    documento: string;
    codigoTransacao: number;
    historico: string;
    bandeira: string;
    status: number;
    contaFinanceiraId: number;
    contaFinanceiraName: string;
    categoriaFinanceiraId: number;
    categoriaFinanceiraName: string;
    formaPagamentoId: number;
    formaPagamentoName: string;
}

export interface CreateContaReceberType {
    valor: number;
    taxa: number;
    vencimento: string;
    documento: string;
    codigoTransacao: number;
    historico: string;
    bandeira: string;
    empresaId: number;
    contaFinanceiraId: number;
    categoriaFinanceiraId: number;
    formaPagamentoId: number;
}

export interface UpdateContaReceberType {
    id: number;
    valor: number;
    taxa: number;
    vencimento: string;
    dataEmissao?: string;
    dataBaixa?: string;
    documento: string;
    codigoTransacao: number;
    historico: string;
    bandeira: string;
    status: number;
    contaFinanceiraId: number;
    contaFinanceiraName?: string;
    categoriaFinanceiraId: number;
    categoriaFinanceiraName?: string;
    formaPagamentoId: number;
    formaPagamentoName?: string;
}

export interface EfetuarBaixaReceberRequest {
    contaReceberId: number;
    dataBaixa: string;
    valorPago: number;
    observacao?: string;
}

export default function useContaReceber() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listContaReceber, setListContaReceber] = useState<ContaReceberType[]>([]);
    const [listContaReceberByEmpresa, setListContaReceberByEmpresa] = useState<ContaReceberType[]>([]);
    const [contaReceberPageCount, setContaReceberPageCount] = useState<number>(0);
    const [initialListContaReceber, setInitialListContaReceber] = useState<ContaReceberType[]>([]);
    const [contaReceber, setContaReceber] = useState<ContaReceberType>({
        id: 0,
        valor: 0,
        taxa: 0,
        vencimento: "",
        dataEmissao: "",
        dataBaixa: "",
        documento: "",
        codigoTransacao: 0,
        historico: "",
        bandeira: "",
        status: 0,
        contaFinanceiraId: 0,
        contaFinanceiraName: "",
        categoriaFinanceiraId: 0,
        categoriaFinanceiraName: "",
        formaPagamentoId: 0,
        formaPagamentoName: "",
    });
    const [contaReceberTotalValor, setContaReceberTotalValor] = useState<number>(0);

    function createContaReceber(createContaReceberType: CreateContaReceberType): Promise<CreateContaReceberType | null> {
        setLoading(true);
        return api
            .post(`/ContaReceber/create`, createContaReceberType)
            .then((response) => {
                const createdContaReceber: CreateContaReceberType = response.data;
                showToast("success", "Conta Receber criada com sucesso.");
                setLoading(false);
                return createdContaReceber;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar conta Receber. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar conta Receber:", error);
                return null;
            });
    }

    function getAllContaReceberPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
        dataInicial?: string,
        dataFinal?: string,
        status?: number,
        codigoTransacao?: number,
        documento?: string,
        historico?: string,
        contaFinanceiraId?: number,
        categoriaFinanceiraId?: number,
        formaPagamentoId?: number
    ): Promise<ContaReceberType[] | null> {
        setLoading(true);
        let url = `/ContaReceber/getAllContaReceberPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        if (dataInicial) {
            url += `&dataInicial=${dataInicial}`;
        }

        if (dataFinal) {
            url += `&dataFinal=${dataFinal}`;
        }

        if (status != undefined) {
            url += `&status=${status}`;
        }

        if (codigoTransacao !== undefined && codigoTransacao > 0) {
            url += `&codigoTransacao=${codigoTransacao}`
        }

        if (documento) {
            url += `&documento=${documento}`;
        }

        if (historico) {
            url += `&historico=${historico}`;
        }

        if (contaFinanceiraId !== undefined && contaFinanceiraId > 0) {
            url += `&contaFinanceiraId=${contaFinanceiraId}`;
        }

        if (categoriaFinanceiraId !== undefined && categoriaFinanceiraId > 0) {
            url += `&categoriaFinanceiraId=${categoriaFinanceiraId}`;
        }

        if (formaPagamentoId !== undefined && formaPagamentoId > 0) {
            url += `&formaPagamentoId=${formaPagamentoId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListContaReceber(response.data.result);
                setInitialListContaReceber(response.data.result);
                setContaReceberPageCount(response.data.pageCount);
                setContaReceberTotalValor(response.data.totalValor);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar contas do financeiro. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar contas do financeiro:", error);
                return null;
            });
    }

    function updateContaReceber(updateContaReceberData: UpdateContaReceberType): Promise<UpdateContaReceberType | null> {
        setLoading(true);
        return api
            .put(`/ContaReceber/${updateContaReceberData.id}`, updateContaReceberData)
            .then((response) => {
                showToast("success", "Conta atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar conta. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar conta:", error);
                return null;
            });
    }

    function deleteContaReceber(contaReceberId: number): Promise<ContaReceberType | null> {
        setLoading(true);
        return api
            .delete(`/ContaReceber/${contaReceberId}`)
            .then(() => {
                setContaReceber({
                    id: 0,
                    valor: 0,
                    taxa: 0,
                    vencimento: "",
                    dataEmissao: "",
                    dataBaixa: "",
                    documento: "",
                    codigoTransacao: 0,
                    historico: "",
                    bandeira: "",
                    status: 0,
                    contaFinanceiraId: 0,
                    contaFinanceiraName: "",
                    categoriaFinanceiraId: 0,
                    categoriaFinanceiraName: "",
                    formaPagamentoId: 0,
                    formaPagamentoName: "",
                });
                showToast("success", "Conta deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar conta. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar conta:", error);
                return null;
            });
    }

    function handleGetContaReceberByEmpresaId(empresaId: number): Promise<ContaReceberType[] | null> {
        setLoading(true);
        return api
            .get(`/ContaReceber/getContaReceberByEmpresaId?empresaId=${empresaId}`)
            .then((response) => {
                const contaReceber: ContaReceberType[] = response.data;
                setListContaReceberByEmpresa(contaReceber);
                setLoading(false);
                return contaReceber;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações das contas do financeiro. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações das contas do financeiro:", error);
                return null;
            });
    }

    function efetuarBaixaContaReceber(request: EfetuarBaixaReceberRequest): Promise<boolean> {
        setLoading(true);
        return api
            .post(`/ContaReceber/efetuar-baixa`, request)
            .then(() => {
                showToast("success", "Baixa efetuada com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao efetuar baixa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao efetuar baixa da conta a receber:", error);
                return false;
            });
    }

    return {
        createContaReceber,
        getAllContaReceberPaged,
        updateContaReceber,
        deleteContaReceber,
        setListContaReceber,
        listContaReceber,
        setContaReceberPageCount,
        contaReceberPageCount,
        setInitialListContaReceber,
        initialListContaReceber,
        setContaReceber,
        contaReceber,
        handleGetContaReceberByEmpresaId,
        setListContaReceberByEmpresa,
        listContaReceberByEmpresa,
        efetuarBaixaContaReceber,
        contaReceberTotalValor,
        setContaReceberTotalValor,
    };
}